import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  InputAdornment, MenuItem, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckIcon from "@mui/icons-material/Check";
import FichaSolicitud, { FichaContenedor, FilaFicha } from "./FichaSolicitud";
import FacturasEditor, { facturaVacia } from "./FacturasEditor";
import { buscarClientesSolicitud, buscarOrdenesCompra, crearSolicitud, fmtMonto, mensajeError, subirArchivosSolicitud } from "../services/solicitudChequeService";

const num = (v) => { const n = Number(String(v ?? "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };

const vacio = (cat) => ({
  dirigido: "", deposito: "", ciudad: cat.ciudad_usuario || "La Paz", servicio: "General", importe: "", moneda: "Bolivianos", concepto: null,
  mes: cat.meses?.[new Date().getMonth()] || "", year: String(new Date().getFullYear()), cod_proyecto: "", observacion: "",
  con_cliente: false, cliente: null, retencion: "", con_factura: false, facturas: [facturaVacia()],
  oc: null, oc_factura: "", oc_tipo: "Proveedores", oc_forma_pago: "Efectivo", oc_moneda: "Bolivianos", oc_monto: "", oc_envio: "",
});


// Nueva solicitud de cheque en dos pasos: llenar el formulario y revisar un resumen antes de enviarla a Contabilidad.
export default function SolicitudFormDialog({ open, catalogos, modo = "normal", onClose, onGuardada }) {
  const esOC = modo === "oc";
  const [ordenes, setOrdenes] = useState([]);
  const [form, setForm] = useState(() => vacio(catalogos));
  const [archivos, setArchivos] = useState([]);
  const [paso, setPaso] = useState("form"); // form | resumen
  const [clientes, setClientes] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const inputArchivos = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(vacio(catalogos));
    setArchivos([]);
    setPaso("form");
    setError("");
  }, [open, catalogos]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const buscarCliente = async (texto) => {
    if (texto.trim().length < 2) return setClientes([]);
    try { const { data } = await buscarClientesSolicitud(texto); setClientes(Array.isArray(data) ? data : []); } catch { setClientes([]); }
  };

  const buscarOC = async (texto) => {
    try { const { data } = await buscarOrdenesCompra(texto); setOrdenes(Array.isArray(data) ? data : []); } catch { setOrdenes([]); }
  };
  useEffect(() => { if (open && esOC) buscarOC(""); }, [open, esOC]);

  const montos = useMemo(() => {
    const imp = num(form.importe);
    const cambio = num(catalogos.cambio);
    if (!imp || !cambio) return { bs: 0, usd: 0 };
    return form.moneda === "Bolivianos" ? { bs: imp, usd: imp / cambio } : { bs: imp * cambio, usd: imp };
  }, [form.importe, form.moneda, catalogos.cambio]);

  const validar = () => {
    if (esOC && !form.oc) return "Elige la orden de compra.";
    if (!form.dirigido.trim()) return "Indica a quién va dirigido el cheque.";
    if (!(num(form.importe) > 0)) return "El importe debe ser mayor a 0.";
    if (!form.concepto) return "Elige el concepto.";
    if (form.con_cliente && !form.cliente) return "Elige el cliente o desmarca «Es por un cliente».";
    if (form.con_factura && form.facturas.some((f) => !f.nro_factura.trim())) return "Cada factura necesita su número.";
    return "";
  };

  const revisar = (e) => {
    e.preventDefault();
    const problema = validar();
    if (problema) return setError(problema);
    setError("");
    setPaso("resumen");
  };

  const enviar = async () => {
    setGuardando(true);
    setError("");
    try {
      const { data } = await crearSolicitud({
        dirigido: form.dirigido.trim(), deposito: form.deposito.trim(), ciudad: form.ciudad, servicio: form.servicio, importe: form.importe, moneda: form.moneda,
        cambio: catalogos.cambio, concepto: form.concepto.concepto, cuenta: form.concepto.cuenta, mes: form.mes, year: form.year, cod_proyecto: form.cod_proyecto || undefined,
        observacion: form.observacion, cod_servicio_cliente: form.con_cliente ? form.cliente?.id : undefined, con_retencion: !!form.retencion, retencion: form.retencion,
        con_factura: form.con_factura, facturas: form.con_factura ? form.facturas : [],
        orden_compra: esOC ? { id_orden_compra: form.oc.id, num_factura: form.oc_factura, tipo: form.oc_tipo, forma_pago: form.oc_forma_pago, moneda: form.oc_moneda, monto: form.oc_monto, costo_envio: form.oc_envio } : undefined,
      });
      let aviso = "";
      if (archivos.length) {
        try { await subirArchivosSolicitud(data.id, archivos); } catch (e2) { aviso = `La solicitud se registró, pero no se pudieron subir los archivos: ${mensajeError(e2, "error desconocido")}`; }
      }
      onGuardada({ id: data.id, aviso });
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar la solicitud."));
      setPaso("form");
    } finally {
      setGuardando(false);
    }
  };

  const proyecto = catalogos.proyectos?.find((p) => String(p.id) === String(form.cod_proyecto));
  const simbolo = form.moneda === "Bolivianos" ? "Bs" : "$us";

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth component="form" onSubmit={revisar} scroll="paper">
      <DialogTitle sx={{ py: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
          <Typography component="span" sx={{ fontSize: 18, fontWeight: 800 }}>
            {esOC ? "Solicitud de cheque · Orden de compra" : "Nueva solicitud de cheque"}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }} aria-label="Paso de la solicitud">
            <Chip size="small" label="1 · Datos" color={paso === "form" ? "primary" : "default"} variant={paso === "form" ? "filled" : "outlined"} />
            <Chip size="small" label="2 · Revisión" color={paso === "resumen" ? "primary" : "default"} variant={paso === "resumen" ? "filled" : "outlined"} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 400, mt: 0.25 }}>
          {paso === "form" ? `Tipo de cambio de hoy: ${catalogos.cambio ?? "—"} Bs por dólar` : "Así la verá Contabilidad. Si todo está bien, envíala."}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5 }}>

        {paso === "form" ? (
          <Stack spacing={1}>
            {esOC && (
              <FichaContenedor>
                <FilaFicha campos={[{ etiqueta: "Orden de compra *", control: (
                  <Autocomplete
                    fullWidth size="small" options={ordenes} value={form.oc} onChange={(_, v) => setForm((f) => ({ ...f, oc: v }))} filterOptions={(x) => x}
                    onInputChange={(_, v, motivo) => motivo === "input" && buscarOC(v)} noOptionsText="Sin resultados"
                    getOptionLabel={(o) => `OC ${o.id} · ${o.proveedor}${o.cliente ? ` · ${o.cliente}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(p) => <TextField {...p} hiddenLabel placeholder="Busca por número, proveedor o cliente" slotProps={{ htmlInput: { ...p.inputProps, "aria-label": "Orden de compra" } }} />}
                  />
                ) }]}
                />
                <FilaFicha campos={[
                  { etiqueta: "Nro. de factura", control: <TextField fullWidth size="small" hiddenLabel value={form.oc_factura} onChange={set("oc_factura")} slotProps={{ htmlInput: { "aria-label": "Nro. de factura" } }} /> },
                  { etiqueta: "Tipo de pago", control: (
                    <TextField fullWidth size="small" select hiddenLabel value={form.oc_tipo} onChange={set("oc_tipo")} slotProps={{ htmlInput: { "aria-label": "Tipo de pago" } }}>
                      {(catalogos.tipos_pago_oc || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </TextField>
                  ) },
                ]}
                />
                <FilaFicha campos={[
                  { etiqueta: "Forma de pago", control: (
                    <TextField fullWidth size="small" select hiddenLabel value={form.oc_forma_pago} onChange={set("oc_forma_pago")} slotProps={{ htmlInput: { "aria-label": "Forma de pago" } }}>
                      {(catalogos.formas_pago_oc || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </TextField>
                  ) },
                  { etiqueta: "Moneda factura", control: (
                    <TextField fullWidth size="small" select hiddenLabel value={form.oc_moneda} onChange={set("oc_moneda")} slotProps={{ htmlInput: { "aria-label": "Moneda de la factura" } }}>
                      <MenuItem value="Bolivianos">Bolivianos</MenuItem>
                      <MenuItem value="Dolares">Dólares</MenuItem>
                    </TextField>
                  ) },
                ]}
                />
                <FilaFicha campos={[
                  { etiqueta: "Monto factura", control: <TextField fullWidth size="small" hiddenLabel value={form.oc_monto} onChange={set("oc_monto")} slotProps={{ htmlInput: { inputMode: "decimal", "aria-label": "Monto de factura" } }} /> },
                  { etiqueta: "Costo del envío", control: <TextField fullWidth size="small" hiddenLabel value={form.oc_envio} onChange={set("oc_envio")} slotProps={{ htmlInput: { inputMode: "decimal", "aria-label": "Costo del envío" } }} /> },
                ]}
                />
              </FichaContenedor>
            )}

            <FichaContenedor>
              <FilaFicha campos={[
                { etiqueta: "Fecha de registro", valor: new Date().toISOString().slice(0, 10) },
                { etiqueta: "Tipo de cambio", valor: catalogos.cambio },
              ]}
              />
              <FilaFicha campos={[{ etiqueta: "Dirigido a *", control: (
                <Autocomplete
                  fullWidth size="small" options={catalogos.nombres || []} value={form.dirigido || null}
                  onChange={(_, v) => setForm((f) => ({ ...f, dirigido: v || "" }))}
                  noOptionsText="Sin resultados: el nombre debe estar en la lista"
                  renderInput={(p) => <TextField {...p} hiddenLabel placeholder="Busca y elige un nombre de la lista" slotProps={{ htmlInput: { ...p.inputProps, "aria-label": "Dirigido a" } }} />}
                />
              ) }]}
              />
              <FilaFicha campos={[{ etiqueta: "Depositado a", control: (
                <Autocomplete
                  fullWidth size="small" options={catalogos.nombres || []} value={form.deposito || null}
                  onChange={(_, v) => setForm((f) => ({ ...f, deposito: v || "" }))}
                  noOptionsText="Sin resultados: el nombre debe estar en la lista"
                  renderInput={(p) => <TextField {...p} hiddenLabel placeholder="Busca y elige un nombre de la lista" slotProps={{ htmlInput: { ...p.inputProps, "aria-label": "Depositado a" } }} />}
                />
              ) }]}
              />
              <FilaFicha campos={[
                { etiqueta: "Ciudad destino", control: (
                  <TextField fullWidth size="small" select hiddenLabel value={form.ciudad} onChange={set("ciudad")} slotProps={{ htmlInput: { "aria-label": "Ciudad de destino" } }}>
                    {(catalogos.ciudades || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                ) },
                { etiqueta: "Tipo de servicio", control: (
                  <TextField fullWidth size="small" select hiddenLabel value={form.servicio} onChange={set("servicio")} slotProps={{ htmlInput: { "aria-label": "Tipo de servicio" } }}>
                    {(catalogos.servicios || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                ) },
              ]}
              />
              <FilaFicha campos={[
                { etiqueta: "Moneda", control: (
                  <ToggleButtonGroup
                    exclusive fullWidth size="small" color="primary" value={form.moneda} onChange={(_, v) => v && setForm((f) => ({ ...f, moneda: v }))}
                    sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, py: 0.5, gap: 0.5 }, "&& .Mui-selected, && .Mui-selected:hover": { bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 800 } }}
                  >
                    <ToggleButton value="Bolivianos">{form.moneda === "Bolivianos" && <CheckIcon sx={{ fontSize: 16 }} />}Bolivianos</ToggleButton>
                    <ToggleButton value="Dolares">{form.moneda === "Dolares" && <CheckIcon sx={{ fontSize: 16 }} />}Dólares</ToggleButton>
                  </ToggleButtonGroup>
                ) },
                { etiqueta: "Tipo de egreso", control: (
                  <TextField fullWidth size="small" select hiddenLabel value={form.retencion} onChange={set("retencion")} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Tipo de egreso" } }}>
                    <MenuItem value="">Sin retención</MenuItem>
                    {(catalogos.retenciones || []).map((r) => <MenuItem key={r.id} value={r.nombre}>{r.nombre} ({r.calculo}%)</MenuItem>)}
                  </TextField>
                ) },
              ]}
              />
              <FilaFicha campos={[
                { etiqueta: "Importe *", control: (
                  <TextField
                    fullWidth size="small" hiddenLabel value={form.importe} onChange={set("importe")} placeholder="0.00"
                    slotProps={{ input: { startAdornment: <InputAdornment position="start">{simbolo}</InputAdornment> }, htmlInput: { inputMode: "decimal", "aria-label": "Importe" } }}
                  />
                ) },
                { etiqueta: "Equivale a", valor: montos.bs > 0 ? (form.moneda === "Bolivianos" ? `$us ${fmtMonto(montos.usd)}` : `Bs ${fmtMonto(montos.bs)}`) : "" },
              ]}
              />
              <FilaFicha campos={[{ etiqueta: "Concepto *", control: (
                <Autocomplete
                  fullWidth size="small" options={catalogos.conceptos || []} value={form.concepto} onChange={(_, v) => setForm((f) => ({ ...f, concepto: v }))}
                  getOptionLabel={(o) => o.concepto || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderOption={(props, o) => <li {...props} key={o.id}>{o.concepto}<Typography component="span" sx={{ ml: 1, fontSize: 11.5, color: "text.secondary" }}>{o.cuenta}</Typography></li>}
                  renderInput={(p) => <TextField {...p} hiddenLabel slotProps={{ htmlInput: { ...p.inputProps, "aria-label": "Concepto" } }} />}
                />
              ) }]}
              />
              <FilaFicha campos={[{ etiqueta: "Mes de servicio", control: (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField fullWidth size="small" select hiddenLabel value={form.mes} onChange={set("mes")} slotProps={{ htmlInput: { "aria-label": "Mes del servicio" } }}>
                    {(catalogos.meses || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </TextField>
                  <TextField size="small" hiddenLabel value={form.year} onChange={set("year")} sx={{ width: 90 }} slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 4, "aria-label": "Año" } }} />
                </Box>
              ) }]}
              />
              <FilaFicha campos={[{ etiqueta: "Proyecto", control: (
                <TextField fullWidth size="small" select hiddenLabel value={form.cod_proyecto} onChange={set("cod_proyecto")} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Proyecto" } }}>
                  <MenuItem value="">Sin proyecto</MenuItem>
                  {(catalogos.proyectos || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
                </TextField>
              ) }]}
              />
              <FilaFicha campos={[{ etiqueta: "Observación", alto: true, control: (
                <TextField fullWidth size="small" hiddenLabel multiline minRows={2} value={form.observacion} onChange={set("observacion")} slotProps={{ htmlInput: { maxLength: 500, "aria-label": "Observación" } }} />
              ) }]}
              />
            </FichaContenedor>

            <FichaContenedor>
              <FilaFicha campos={[{ etiqueta: "Cliente", control: (
                <Box>
                  <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={form.con_cliente} onChange={(e) => setForm((f) => ({ ...f, con_cliente: e.target.checked }))} />} label="Es por un cliente" />
                  {form.con_cliente && (
                    <Autocomplete
                      fullWidth size="small" options={clientes} value={form.cliente} onChange={(_, v) => setForm((f) => ({ ...f, cliente: v }))}
                      onInputChange={(_, v, motivo) => motivo === "input" && buscarCliente(v)} filterOptions={(x) => x} noOptionsText="Escribe al menos 2 letras"
                      getOptionLabel={(o) => `${o.razon_social} · ${o.servicio}${o.descripcion ? ` · ${o.descripcion}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(p) => <TextField {...p} hiddenLabel placeholder="Busca por razón social o descripción" slotProps={{ htmlInput: { ...p.inputProps, "aria-label": "Cliente y servicio" } }} />}
                    />
                  )}
                </Box>
              ) }]}
              />
              <FilaFicha campos={[{ etiqueta: "Factura", control: (
                <Box>
                  <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={form.con_factura} onChange={(e) => setForm((f) => ({ ...f, con_factura: e.target.checked }))} />} label="Tiene factura" />
                  {form.con_factura && <FacturasEditor facturas={form.facturas} onChange={(facturas) => setForm((f) => ({ ...f, facturas }))} />}
                </Box>
              ) }]}
              />
              <FilaFicha campos={[{ etiqueta: "Archivos", control: (
                <Box>
                  <input ref={inputArchivos} type="file" multiple style={{ display: "none" }} onChange={(e) => { setArchivos((a) => [...a, ...Array.from(e.target.files)].slice(0, 10)); e.target.value = ""; }} />
                  <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} sx={{ textTransform: "none" }} onClick={() => inputArchivos.current?.click()}>Adjuntar archivos</Button>
                  <Typography component="span" sx={{ ml: 1, fontSize: 12, color: "text.secondary" }}>Hasta 10 archivos</Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: archivos.length ? 1 : 0 }}>
                    {archivos.map((f, i) => <Chip key={`${f.name}${i}`} size="small" label={f.name} onDelete={() => setArchivos((a) => a.filter((_, j) => j !== i))} />)}
                  </Box>
                </Box>
              ) }]}
              />
            </FichaContenedor>
          </Stack>
        ) : (
          <FichaSolicitud
            fecha={new Date().toISOString().slice(0, 10)} cambio={catalogos.cambio} dirigido={form.dirigido} deposito={form.deposito} ciudad={form.ciudad}
            servicio={form.servicio} moneda={form.moneda} tipoEgreso={form.retencion} importeBs={fmtMonto(montos.bs)} importeUsd={fmtMonto(montos.usd)}
            concepto={form.concepto?.concepto} mes={`${form.mes} del ${form.year}`} proyecto={proyecto?.nombre} observacion={form.observacion}
            extras={[
              esOC && { etiqueta: "Orden de compra", valor: form.oc && `OC ${form.oc.id} · ${form.oc.proveedor}` },
              form.con_cliente && { etiqueta: "Cliente", valor: form.cliente && `${form.cliente.razon_social} · ${form.cliente.servicio}` },
              form.con_factura && { etiqueta: "Facturas", valor: form.facturas.map((x) => x.nro_factura).join(", ") },
              { etiqueta: "Archivos", valor: archivos.map((x) => x.name).join(", ") },
            ]}
          />
        )}
      </DialogContent>

      <DialogActions>
        {paso === "form" ? (
          <>
            <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancelar</Button>
            <Button type="submit" variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>Revisar solicitud</Button>
          </>
        ) : (
          <>
            <Button onClick={() => setPaso("form")} disabled={guardando} sx={{ textTransform: "none" }}>Volver y editar</Button>
            <Button variant="contained" onClick={enviar} disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>
              {guardando ? <CircularProgress size={20} color="inherit" /> : "Enviar solicitud"}
            </Button>
          </>
        )}
      </DialogActions>
      {/* Aviso flotante: se ve aunque el formulario esté desplazado hacia abajo */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={(_, motivo) => motivo !== "clickaway" && setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Dialog>
  );
}
